'use client';

import * as React from 'react';
import {
  CaretSortIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@radix-ui/react-icons';
import * as Sentry from '@sentry/nextjs';
import { Select as SelectPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';

type SelectRootProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Root
>;

/**
 * Desync-safe wrapper around Radix `Select.Root`.
 *
 * Radix has a long-standing bug (radix-ui/primitives#1569): when a Controlled
 * `<Select>` transitions `value` from a real string to `undefined`/`null`/`''`,
 * Radix interprets the falsy value as "switch to uncontrolled mode" and keeps
 * its previous internal selection + displayed label. The form holds nothing,
 * the trigger keeps showing the last label, and any subsequent validation
 * rejects the empty form value with a contradictory error.
 *
 * Defense:
 *   - Keep the Select in ONE mode for its lifetime, decided by whether the
 *     `value` PROP IS PRESENT — not by whether its value happens to be
 *     defined. Empty controlled values are normalised to `''`, never to
 *     `undefined`. See the mode-flip note on `isControlled` below.
 *   - Detect set→unset transitions and bump a remount key. Radix gets a fresh
 *     instance with no lingering state; the trigger reverts to the placeholder
 *     atomically. Set→same and unset→set don't remount (no flicker).
 *   - Emit a Sentry breadcrumb on each clear so any downstream capture
 *     (validation block, server action error) shows the value transition in
 *     its breadcrumb chain.
 */
const Select: React.FC<SelectRootProps> = (rootProps) => {
  const { value, name, ...props } = rootProps;

  // Radix decides controlled-ness with `prop !== undefined`
  // (`@radix-ui/react-use-controllable-state`), so ANY Select whose `value`
  // starts undefined and later resolves mounts UNCONTROLLED and flips to
  // controlled on the user's first pick, logging "Select is changing from
  // uncontrolled to controlled" (Sentry BALLEE-H0). That warning reaches
  // production: the published Radix build hardcodes its dev guard to
  // `if (true)`, so no bundler can strip it.
  //
  // Keying off PROP PRESENCE rather than definedness is what makes the fix
  // hold repo-wide. 19 call sites write the react-hook-form idiom
  // `value={field.value ?? undefined}` — every one is a controlled Select
  // whose first render is empty, and every one flipped mode. `hasOwn` reads
  // all of them as controlled from mount, so no call site has to change,
  // while a consumer that omits `value` entirely (driving the Select with
  // `defaultValue`, or with Radix's own internal state) stays uncontrolled.
  //
  // `''` is the correct controlled-empty value — Radix's own
  // `shouldShowPlaceholder` is `value === '' || value === undefined`, so the
  // placeholder still renders and nothing errors.
  // `in`, not `Object.hasOwn` — this package's `lib` target predates ES2022.
  const isControlled = 'value' in rootProps;
  const selectedValue = isControlled ? (value ?? '') : undefined;
  const isSet = selectedValue != null && selectedValue !== '';

  // Refs are read AND mutated during render here. ESLint's `react-hooks/refs`
  // rule warns against this pattern in the general case (refs read during
  // render produce stale values), but for this specific use it is correct:
  //   - The `key` prop on `SelectPrimitive.Root` MUST change synchronously on
  //     the same render that detects a set→unset transition. Deferring the
  //     bump to a useEffect would leave Radix mounted with stale state for
  //     one render — exactly the desync bug we're fixing.
  //   - `prevSetRef` is the canonical "previous-render value" pattern from
  //     React's own docs; mutating it during render to compare against the
  //     next render's value is intended.
  // The community fix for radix-ui/primitives#1569 uses this pattern.
  /* eslint-disable react-hooks/refs */
  const remountKeyRef = React.useRef(0);
  const prevSetRef = React.useRef(isSet);

  if (prevSetRef.current && !isSet) {
    remountKeyRef.current += 1;
    Sentry.addBreadcrumb({
      category: 'ui.select',
      level: 'info',
      message: 'Select value cleared',
      ...(name ? { data: { name } } : {}),
    });
  }
  prevSetRef.current = isSet;
  const remountKey = remountKeyRef.current;
  /* eslint-enable react-hooks/refs */

  return (
    <SelectPrimitive.Root
      key={remountKey}
      value={selectedValue}
      name={name}
      {...props}
    />
  );
};
Select.displayName = 'Select';

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger: React.FC<
  React.ComponentPropsWithRef<typeof SelectPrimitive.Trigger>
> = ({ className, children, ...props }) => (
  <SelectPrimitive.Trigger
    className={cn(
      'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm whitespace-nowrap shadow-2xs focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <CaretSortIcon className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton: React.FC<
  React.ComponentPropsWithRef<typeof SelectPrimitive.ScrollUpButton>
> = ({ className, ...props }) => (
  <SelectPrimitive.ScrollUpButton
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpButton>
);
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton: React.FC<
  React.ComponentPropsWithRef<typeof SelectPrimitive.ScrollDownButton>
> = ({ className, ...props }) => (
  <SelectPrimitive.ScrollDownButton
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
);
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent: React.FC<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
> = ({ className, children, position = 'popper', ...props }) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border shadow-md',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1.5',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel: React.FC<
  React.ComponentPropsWithRef<typeof SelectPrimitive.Label>
> = ({ className, ...props }) => (
  <SelectPrimitive.Label
    className={cn('px-2 py-1.5 text-sm font-semibold', className)}
    {...props}
  />
);
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem: React.FC<
  React.ComponentPropsWithRef<typeof SelectPrimitive.Item> & {
    description?: React.ReactNode;
  }
> = ({ className, children, description, ...props }) => (
  <SelectPrimitive.Item
    className={cn(
      'focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    {description ? (
      <div className="flex flex-col">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        <span className="text-muted-foreground text-xs">{description}</span>
      </div>
    ) : (
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    )}
  </SelectPrimitive.Item>
);
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator: React.FC<
  React.ComponentPropsWithRef<typeof SelectPrimitive.Separator>
> = ({ className, ...props }) => (
  <SelectPrimitive.Separator
    className={cn('bg-muted -mx-1 my-1 h-px', className)}
    {...props}
  />
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
